import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/config/constants";
import { PageLoader } from "@/components/ui/Loader";
import { ModalRegistroHorimetro } from "@/pages/Locacoes/ModalRegistroHorimetro";
import {
  createRegistroHorimetro,
  extractApiError,
  fetchLocacoesSnapshot,
  type LocacoesSnapshot,
} from "@/services/locacoes";
import { filterMaquinasPesadas, findContratoAtivoParaMaquina } from "./operadorUtils";

export function OperadorHorimetroPage() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<LocacoesSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSnapshot(await fetchLocacoesSnapshot());
    } catch (e) {
      setError(extractApiError(e, "Erro ao carregar máquinas."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const maquinarios = useMemo(
    () => filterMaquinasPesadas(snapshot?.maquinariosScoped ?? snapshot?.maquinarios ?? []),
    [snapshot],
  );

  const handleSave = async (values: Parameters<typeof createRegistroHorimetro>[0]) => {
    setBusy(true);
    try {
      const contrato = findContratoAtivoParaMaquina(snapshot?.contratos ?? [], values.maquinaId);
      const registro = await createRegistroHorimetro({
        ...values,
        ...(contrato ? { contratoId: contrato.id } : {}),
      });
      setSnapshot((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          registrosHorimetro: [registro, ...prev.registrosHorimetro],
        };
      });
      return registro;
    } catch (e) {
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setFormOpen(false);
    navigate(ROUTES.OPERADOR);
  };

  if (loading && !snapshot) {
    return <PageLoader label="Carregando..." />;
  }

  if (maquinarios.length === 0) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-900">
        {error ?? "Nenhuma máquina pesada vinculada ao seu contrato ativo."}
      </div>
    );
  }

  return (
    <>
      {error && (
        <div className="mx-auto mb-4 max-w-lg rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      <ModalRegistroHorimetro
        open={formOpen}
        editing={null}
        maquinarios={maquinarios}
        accessMode="operador"
        busy={busy}
        onClose={handleClose}
        onSave={handleSave}
        onMutationComplete={() => {
          setFormOpen(false);
          navigate(ROUTES.OPERADOR);
        }}
      />
    </>
  );
}
